import { Box, Card, IconButton, Stack, Typography } from '@mui/material';

import type {
  ChatCardItem,
  ChatCardItemState,
  ChatCardTone,
} from '@karehero/models';

import { Icon } from '@/components/atoms/Icon/Icon';
import type { Icons } from '@/components/atoms/Icon/icons';
import { elevation } from '@/theme/elevation';

export interface ChatCardProps {
  title: string;
  /** One line each. */
  lines?: string[];
  /** Drawn as a checklist instead of lines, each ticked as it is done. */
  items?: ChatCardItem[];
  /** What was heard reads as theirs, so it is a step darker than our prompts. */
  tone?: ChatCardTone;
  /** Held at the top of the chat while it is being worked through. */
  isPinned?: boolean;
  /** Offers to correct the lines; omit when they cannot be. */
  onEdit?: () => void;
  /** Pins it, or unpins it while pinned; omit when it cannot be moved. */
  onPin?: () => void;
  /** Held over the chat as it scrolls under it, it lifts off it with a soft shadow; omit for none. */
  isLifted?: boolean;
}

/** How long the card takes to lift. */
const LIFT_MS = 240;

const ITEM_ICON: Record<ChatCardItemState, Icons> = {
  done: 'check-circle',
  doing: 'dot-circle',
  waiting: 'circle',
};

const ITEM_COLOUR: Record<ChatCardItemState, string> = {
  done: 'success.main',
  doing: 'custom.coral.600',
  waiting: 'text.disabled',
};

const ChecklistItem = ({ label, state, found }: ChatCardItem) => (
  <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
    <Box
      sx={{
        display: 'flex',
        flexShrink: 0,
        color: ITEM_COLOUR[state],
        transition: 'color 300ms ease',
      }}
    >
      <Icon icon={ITEM_ICON[state]} fontSize={14} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      {/* Neutral 600 for what is still to come: text.secondary fails contrast on the card. */}
      <Typography
        variant='body2'
        sx={{
          color: state === 'waiting' ? 'custom.neutral.600' : 'text.primary',
        }}
      >
        {label}
      </Typography>
      {/* What was taken for it, so a mistake is seen as it is made. */}
      {state === 'done' && found && (
        <Typography variant='caption' sx={{ color: 'custom.neutral.600' }}>
          {found}
        </Typography>
      )}
    </Box>
  </Stack>
);

/**
 * ChatCard is something given back in the conversation, from us rather than
 * said by Keni: prompts to answer with, what was heard, how we can help, or
 * the work being done while what they said is read.
 */
export const ChatCard = ({
  title,
  lines = [],
  items,
  tone = 'help',
  isPinned,
  onEdit,
  onPin,
  isLifted,
}: ChatCardProps) => (
  <Card
    variant='padded'
    sx={[
      { width: '100%' },
      // Only a card told whether to lift takes a shadow at all.
      isLifted !== undefined && {
        boxShadow: elevation[isLifted ? 4 : 0],
        transition: `box-shadow ${LIFT_MS}ms ease-in-out`,
      },
    ]}
  >
    <Stack spacing={1.5}>
      <Stack direction='row' spacing={0.75} sx={{ alignItems: 'center' }}>
        <Box sx={{ display: 'flex', color: 'custom.coral.600' }}>
          <Icon icon='sparkles' fontSize={12} />
        </Box>
        <Typography variant='subtitle1' sx={{ flex: 1 }}>
          {title}
        </Typography>
        {onPin && (
          <IconButton
            size='small'
            aria-label={isPinned ? 'Unpin' : 'Pin to the top'}
            aria-pressed={!!isPinned}
            onClick={onPin}
            sx={{ color: isPinned ? 'text.secondary' : 'text.disabled' }}
          >
            <Icon
              icon='thumbtack'
              type={isPinned ? 'fas' : 'fal'}
              fontSize={14}
            />
          </IconButton>
        )}
        {isPinned && !onPin && (
          <Box
            role='img'
            aria-label='Pinned'
            sx={{ display: 'flex', color: 'text.secondary' }}
          >
            <Icon icon='thumbtack' type='fas' fontSize={14} />
          </Box>
        )}
        {onEdit && (
          <IconButton size='small' aria-label='Edit' onClick={onEdit}>
            <Icon icon='pencil' fontSize={14} />
          </IconButton>
        )}
      </Stack>
      {items ? (
        <Stack spacing={1}>
          {items.map((item) => (
            <ChecklistItem key={item.label} {...item} />
          ))}
        </Stack>
      ) : (
        <Stack spacing={1}>
          {lines.map((line) => (
            <Typography
              key={line}
              variant='body1'
              sx={{
                color: tone === 'heard' ? 'text.primary' : 'custom.neutral.600',
              }}
            >
              {line}
            </Typography>
          ))}
        </Stack>
      )}
    </Stack>
  </Card>
);

ChatCard.displayName = 'ChatCard';
export default ChatCard;
